import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  UserCog,
  Mail,
  ShieldCheck,
  BadgeCheck,
  XCircle,
  Save,
  RefreshCw,
  Linkedin,
  MessageSquareText,
  User as UserIcon,
  Loader2,
} from 'lucide-react';
import { storage } from '../../Shared/Utils/authStorage';
import { getUserById, updateUser } from '../../App/Services/UserService';
import { UserForm } from '../../App/Types/UsersType';
import { useFormController } from '../../Shared/Component/readFormData/useFormController';
import { Alert, AlertProps } from '../../Shared/Utils/Alert';

interface ProfileForm {
  firstName: string;
  lastName: string;
  linkedInUrl: string;
  message: string;
}

const inputClass =
  'w-full rounded-xl border border-slate-800 bg-slate-950/60 px-4 py-3 text-sm font-medium text-slate-100 placeholder:text-slate-500 focus:border-cyan-500/60 focus:outline-none focus:ring-1 focus:ring-cyan-500/40 transition-colors';

const labelClass = 'mb-1.5 block text-[11px] font-bold uppercase tracking-widest text-slate-400';

const AdminProfileComponent = () => {
  const [user, setUser] = useState<UserForm | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState<AlertProps | null>(null);

  const { values, setAllValues, handleInputChange } = useFormController<ProfileForm>({
    firstName: '',
    lastName: '',
    linkedInUrl: '',
    message: '',
  });

  const loadProfile = async () => {
    setAlert(null);
    setLoading(true);
    const userId = storage.getUserId();
    if (!userId) {
      setAlert({
        variant: 'error',
        title: 'Session Error',
        message: 'No logged-in user found. Please sign in again.',
        autoCloseMs: 5000,
        onClose: () => setAlert(null),
      });
      setLoading(false);
      return;
    }
    try {
      const res = await getUserById(userId);
      if (res.success && res.data) {
        setUser(res.data);
        setAllValues({
          firstName: res.data.firstName || '',
          lastName: res.data.lastName || '',
          linkedInUrl: res.data.linkedInUrl || '',
          message: res.data.message || '',
        });
      } else {
        setAlert({
          variant: 'error',
          title: 'Load Error',
          message: res.message || 'Could not retrieve admin profile.',
          autoCloseMs: 5000,
          onClose: () => setAlert(null),
        });
      }
    } catch {
      setAlert({
        variant: 'error',
        title: 'Load Error',
        message: 'An unexpected error occurred while loading the profile.',
        autoCloseMs: 5000,
        onClose: () => setAlert(null),
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    if (!user) return;
    setAlert(null);
    setSaving(true);
    try {
      const res = await updateUser(
        {
          firstName: values.firstName,
          lastName: values.lastName,
          linkedInUrl: values.linkedInUrl,
          message: values.message,
        },
        user.id
      );
      if (res.success && res.data) {
        setUser(res.data);
        setAlert({
          variant: 'success',
          title: 'Success...!',
          message: 'Admin profile has been updated successfully.',
          autoCloseMs: 4000,
          onClose: () => setAlert(null),
        });
      } else {
        setAlert({
          variant: 'error',
          title: 'Update Error',
          message: res.message || 'Could not update the admin profile.',
          autoCloseMs: 5000,
          onClose: () => setAlert(null),
        });
      }
    } catch {
      setAlert({
        variant: 'error',
        title: 'Update Error',
        message: 'An unexpected error occurred while saving the profile.',
        autoCloseMs: 5000,
        onClose: () => setAlert(null),
      });
    } finally {
      setSaving(false);
    }
  };

  const initials =
    `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`.toUpperCase() || 'A';
  const fullName =
    `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || 'Nexgent Admin';

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
      {alert && <Alert {...alert} />}

      <div className="mx-auto max-w-6xl space-y-8">
        <header className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
              <UserCog size={26} />
            </span>
            <div>
              <h1 className="text-2xl font-black tracking-tight text-slate-100 md:text-3xl">Admin Profile</h1>
              <p className="mt-1 text-sm font-medium text-slate-400">View and update your administrator account details.</p>
            </div>
          </div>
          <button
            onClick={loadProfile}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-800/80 px-4 py-2.5 text-sm font-bold text-slate-200 transition-colors hover:border-cyan-500/40 hover:text-cyan-300 disabled:opacity-50"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </header>

        {loading ? (
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="h-72 animate-pulse rounded-2xl border border-slate-800 bg-slate-900/40 lg:col-span-1" />
            <div className="h-96 animate-pulse rounded-2xl border border-slate-800 bg-slate-900/40 lg:col-span-2" />
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="grid gap-6 lg:grid-cols-3"
          >
            <div className="relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/60 p-6 lg:col-span-1">
              <div className="pointer-events-none absolute -top-16 -right-16 h-48 w-48 rounded-full bg-cyan-500/10 blur-3xl" />
              <div className="flex flex-col items-center text-center">
                <span className="flex h-24 w-24 items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 text-3xl font-black text-white shadow-lg shadow-cyan-500/20">
                  {initials}
                </span>
                <h2 className="mt-4 text-lg font-black text-slate-100">{fullName}</h2>
                <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
                  <span className="inline-flex items-center gap-1 rounded-full border border-violet-500/30 bg-violet-500/10 px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider text-violet-400">
                    <ShieldCheck size={12} /> {user?.role || 'ADMIN'}
                  </span>
                  <span
                    className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                      user?.status?.toUpperCase() === 'ACTIVE' || user?.isVerified
                        ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400'
                        : 'border-rose-500/30 bg-rose-500/10 text-rose-400'
                    }`}
                  >
                    {user?.isVerified ? <BadgeCheck size={12} /> : <XCircle size={12} />}
                    {user?.status || (user?.isVerified ? 'VERIFIED' : 'INACTIVE')}
                  </span>
                </div>
                <p className="mt-3 flex items-center gap-1.5 text-sm font-medium text-slate-400">
                  <Mail size={14} /> {user?.email || '—'}
                </p>
                <a
                  href={user?.linkedInUrl || undefined}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-cyan-400 hover:text-cyan-300"
                >
                  <Linkedin size={14} /> LinkedIn
                </a>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-3 border-t border-slate-800 pt-5 text-center">
                <div className="rounded-xl border border-slate-800 bg-slate-950/40 p-3">
                  <p className="text-lg font-black text-slate-100">{user?.inviteCountRemaining ?? 0}</p>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Invites Left</p>
                </div>
                <div className="rounded-xl border border-slate-800 bg-slate-950/40 p-3">
                  <p className="text-lg font-black text-slate-100">
                    {user?.createdAt ? new Date(user.createdAt).toLocaleDateString(undefined, { month: 'short', year: 'numeric' }) : '—'}
                  </p>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Member Since</p>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 lg:col-span-2">
              <div className="mb-5 flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
                  <UserIcon size={18} />
                </span>
                <div>
                  <h3 className="text-base font-black text-slate-100">Personal Information</h3>
                  <p className="text-xs font-medium text-slate-500">Keep your details up to date.</p>
                </div>
              </div>

              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label className={labelClass}>First Name</label>
                  <input className={inputClass} placeholder="Enter first name" value={values.firstName} onChange={handleInputChange('firstName')} />
                </div>
                <div>
                  <label className={labelClass}>Last Name</label>
                  <input className={inputClass} placeholder="Enter last name" value={values.lastName} onChange={handleInputChange('lastName')} />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelClass}>Email (read only)</label>
                  <input className={inputClass} value={user?.email || ''} disabled readOnly />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelClass}>LinkedIn URL</label>
                  <div className="relative">
                    <Linkedin size={16} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
                    <input className={`${inputClass} pl-10`} placeholder="https://linkedin.com/in/..." value={values.linkedInUrl} onChange={handleInputChange('linkedInUrl')} />
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label className={labelClass}>Message / Bio</label>
                  <div className="relative">
                    <MessageSquareText size={16} className="pointer-events-none absolute left-3.5 top-3.5 text-slate-500" />
                    <textarea
                      className={`${inputClass} min-h-[110px] resize-none pl-10`}
                      placeholder="Short bio or message"
                      value={values.message}
                      onChange={handleInputChange('message')}
                    />
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="inline-flex items-center gap-2 rounded-xl bg-cyan-500 px-5 py-3 text-sm font-black text-slate-950 transition-all hover:bg-cyan-400 active:scale-95 disabled:opacity-50"
                >
                  {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default AdminProfileComponent;